import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { errText } from "@/lib/api";
import { Panel, Btn, useOps } from "./ui";
import ArtifactCanvas from "./ArtifactCanvas";
import RegionInspector from "./RegionInspector";
import SignalPanels from "./SignalPanels";
import { usePreview } from "./usePreview";

const EFFECTS = [["crease", "Fold crease"], ["paperclip", "Paper-clip shadow"], ["stamp", "IDENTIFIED stamp"]];

function useBackground(api, tid, version) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/artifacts/${tid}/background`, { responseType: "blob" }).then((r) => { if (live) setUrl(URL.createObjectURL(r.data)); });
    return () => { live = false; };
  }, [api, tid, version]);
  return url;
}

function Editor({ tid, onListChange }) {
  const { api } = useOps();
  const [d, setD] = useState(null);
  const [layout, setLayout] = useState(null);
  const [sel, setSel] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [bgVer, setBgVer] = useState(0);
  const load = useCallback(() => api.get(`/artifacts/${tid}`).then((r) => { setD(r.data); setLayout(r.data.layout); setDirty(false); setSel(null); }), [api, tid]);
  useEffect(() => { load(); }, [load]);
  const signal = d?.style === "signal";
  const preview = usePreview(api, tid, signal ? null : layout);
  const bg = useBackground(api, tid, bgVer);
  if (!d || !layout) return null;

  const update = (next) => { setLayout(next); setDirty(true); };
  const changeRegion = (r) => update({ ...layout, regions: layout.regions.map((x) => (x.id === r.id ? r : x)) });
  const addText = () => {
    let n = 1; while (layout.regions.some((r) => r.id === `text${n}`)) n += 1;
    const r = { id: `text${n}`, kind: "text", x: 80, y: 80, w: Math.round(d.width / 2), h: 50, text: "{serial}", size: 32, bold: false, color: "#1a1816", align: "left" };
    update({ ...layout, regions: [...layout.regions, r] }); setSel(r.id);
  };
  const removeSel = () => { update({ ...layout, regions: layout.regions.filter((r) => r.id !== sel) }); setSel(null); };
  const toggleFx = (k) => update({ ...layout, effects: layout.effects.includes(k) ? layout.effects.filter((e) => e !== k) : [...layout.effects, k] });
  const save = async () => { try { await api.put(`/artifacts/${tid}/layout`, layout); toast.success("Layout saved — new artifacts use it"); setDirty(false); onListChange(); } catch (e) { toast.error(errText(e)); } };
  const reset = async () => { if (!window.confirm("Reset this template's layout to the default?")) return; await api.delete(`/artifacts/${tid}/layout`); load(); onListChange(); };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/content/${d.slot}`, fd); toast.success("Artwork uploaded"); setBgVer((v) => v + 1); setLayout({ ...layout }); onListChange(); }
    catch (e) { toast.error(errText(e)); }
  };
  const fullUrl = async () => {
    const r = await api.post(`/artifacts/${tid}/preview?full=true`, layout, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };

  return (
    <div className="grid gap-3 2xl:grid-cols-[auto_1fr]" data-testid={`artifact-editor-${tid}`}>
      <Panel title={`${d.label} — ${d.width}×${d.height}`} actions={<>
        {dirty && <span className="text-[10px] text-amber-300">unsaved</span>}
        <label className="cursor-pointer rounded border border-[#243144] bg-[#1a2332] px-2.5 py-1.5 text-xs hover:bg-[#223047]">
          {d.has_artwork ? "Replace artwork" : "Upload artwork"}
          <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="artifact-artwork-upload" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <Btn variant="ghost" onClick={addText} data-testid="artifact-add-text">+ Text box</Btn>
        <Btn variant="ghost" onClick={reset} data-testid="artifact-reset-layout">Reset</Btn>
        <Btn onClick={save} disabled={!dirty} data-testid="artifact-save-layout">Save layout</Btn>
      </>}>
        <div className="flex flex-col gap-4 lg:flex-row">
          <ArtifactCanvas tpl={d} layout={layout} bgUrl={bg} selected={sel} onSelect={setSel} onChange={changeRegion} displayWidth={d.width > d.height ? 640 : 460} />
          <div className="w-full space-y-4 lg:w-64">
            <RegionInspector region={layout.regions.find((r) => r.id === sel)} onChange={changeRegion} onDelete={removeSel} />
            {!signal && <div className="space-y-1 border-t border-[#243144] pt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Effects</div>
              {EFFECTS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid={`artifact-effect-${k}`} checked={layout.effects.includes(k)} onChange={() => toggleFx(k)} /> {label}</label>
              ))}
            </div>}
            <div className="border-t border-[#243144] pt-3 text-[11px] text-slate-500">
              Variables: <span className="mono text-sky-300">{d.vars}</span><br />Preview uses test serial <span className="mono">{d.test_vars.serial}</span> and the test face (replace it under Content slots).
            </div>
          </div>
        </div>
      </Panel>
      {signal ? <SignalPanels tpl={d} layout={layout} /> : (
        <Panel title="Live preview" actions={<>{preview.busy && <span className="text-[10px] text-slate-400">rendering…</span>}<Btn variant="ghost" onClick={fullUrl} data-testid="artifact-preview-full">Full size</Btn></>}>
          {preview.url ? <img data-testid="artifact-live-preview" src={preview.url} alt="preview" className="max-h-[75vh] w-auto rounded border border-[#243144]" /> : <div className="text-xs text-slate-500">Rendering…</div>}
        </Panel>
      )}
    </div>
  );
}

export default function Artifacts() {
  const { api } = useOps();
  const [list, setList] = useState([]);
  const [tid, setTid] = useState("dossier");
  const load = useCallback(() => api.get("/artifacts").then((r) => setList(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="admin-artifacts">
      <div className="flex flex-wrap gap-1.5">
        {list.map((t) => (
          <button key={t.id} type="button" onClick={() => setTid(t.id)} data-testid={`artifact-template-${t.id}`}
            className={`rounded border px-2.5 py-1.5 text-left text-xs transition-colors ${tid === t.id ? "border-sky-500 bg-sky-500/15 text-sky-200" : "border-[#243144] bg-[#131a26] text-slate-300 hover:bg-[#1a2332]"}`}>
            {t.label}
            <span className="ml-2 text-[10px] text-slate-500">P{t.phase}{t.has_artwork ? " · art" : ""}{t.customized ? " · edited" : ""}</span>
          </button>
        ))}
      </div>
      <Editor key={tid} tid={tid} onListChange={load} />
    </div>
  );
}
